import { GridPaginationModel } from '@mui/x-data-grid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useAppSelector } from 'src/store';
import { ContactImportSummary } from '../types';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useAssetsApi } from 'src/modules/assetLibrary/api/AssetsApi';
import { useContactsApi } from '../api/ContactsApi';
import { ContactImportLogRow, mapContactImportLogRow } from '../helpers/importLogs';
import { triggerFileDownload } from '../helpers/triggerFileDownload';

type ContactImportLogsState = {
  items: ContactImportSummary[];
  count: number;
};

const DEFAULT_PAGE_SIZE = 25;

export const useContactImportLogsViewController = () => {
  const currentCompanyId = useAppSelector(getCurrentCompanyId);
  const contactsApi = useContactsApi();
  const { getFileDownloadUrl } = useAssetsApi();

  const [isLoading, setIsLoading] = useState(false);
  const [logs, setLogs] = useState<ContactImportLogsState>({
    items: [],
    count: 0,
  });
  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);

  const loadLogs = useCallback(async () => {
    if (!currentCompanyId) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await contactsApi.getContactImports({
        companyId: currentCompanyId,
        limit: pagination.pageSize,
        offset: pagination.page * pagination.pageSize,
      });

      setLogs({
        items: response.data.items,
        count: response.data.count,
      });
    } catch (error) {
      console.error(error);
      toast.error('Failed to load import logs. Please try again.');
      setLogs({
        items: [],
        count: 0,
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentCompanyId, contactsApi, pagination.page, pagination.pageSize]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // Always request a fresh presigned URL right before download — the one returned
  // in the list response is short-lived (10 min TTL) and may already be expired.
  const downloadImportFile = useCallback(
    async (fileId: string) => {
      if (!currentCompanyId || downloadingFileId) {
        return;
      }

      setDownloadingFileId(fileId);

      try {
        const { data } = await getFileDownloadUrl(fileId, currentCompanyId);

        if (!data.url) {
          toast.error('This file is unavailable.');
          return;
        }

        triggerFileDownload(data.url, data.name ?? undefined);
      } catch (error) {
        console.error(error);
        toast.error('This file is unavailable.');
      } finally {
        setDownloadingFileId(null);
      }
    },
    [currentCompanyId, downloadingFileId, getFileDownloadUrl],
  );

  const rows = useMemo<ContactImportLogRow[]>(
    () => logs.items.map(item => mapContactImportLogRow(item)),
    [logs.items],
  );

  return {
    isLoading,
    rows,
    count: logs.count,
    pagination,
    changePagination: setPagination,
    downloadImportFile,
    downloadingFileId,
  };
};
